import React, { FC } from 'react';
import Log from '@lib/Log';

/** @jsx jsx */
import { jsx, css, SerializedStyles } from '@emotion/core';
import { Clear } from '@css/common.elements';
import { useUniqueId } from '@hooks/useUniqueId';
import {
  RadioButtonContainer,
  RadioOption,
  RadioLabelWrapper,
  RadioInput,
  RadioCheckmark,
  RadioLabel,
  RadioNote,
  SlimRBWrapper,
  radioOptionSelectedStyle,
  radioLabelErrorStyle
} from './RadioButton.elements';
import { formInputErrorStyle } from '@css/styles';
import { RadioButtonGender } from './RadioButtonGender';

const prefix = 'radio-button-';

type RadioOptions = {
  text: string | JSX.Element
  value: string | number
  note?: string
}

type Props = {
  name: string
  onChange: Function
  value: string
  error: boolean
  options: Array<RadioOptions>
  displaySlim?: boolean
  hideCheckmark?: boolean
  showBoxShadow?: boolean
}

export const RadioButton: FC<Props> = ({
  name,
  onChange,
  value,
  error,
  options,
  displaySlim,
  hideCheckmark = false,
  showBoxShadow = false,
}) => {
  const ids = options.map(() => useUniqueId(prefix));

  const handleChange = event => {
    // Log.info('RadioButton', `changed ${name}: ${event.target.value}`);
    onChange(name, event.target.value);
  };

  if (name === 'gender') {
    return (
      <RadioButtonGender
        onChange={handleChange}
        value={value}
        error={error}
        displaySlim={displaySlim}
      />
    );
  }

  const Wrapper = displaySlim ? SlimRBWrapper : RadioButtonContainer;

  return (
    <Wrapper>
      { options.map((option, index) => {
        const isChecked = value === option.value;

        const optionStyles: SerializedStyles[] = [];
        if (isChecked) {
          optionStyles.push(radioOptionSelectedStyle)
        }
        if (error) {
          optionStyles.push(formInputErrorStyle)
        }

        return (
          <RadioOption
            key={`${name}-${index}`}
            css={optionStyles}
            showBoxShadow={showBoxShadow}
          >
            <RadioInput
              id={ids[index]}
              type='radio'
              name={name}
              value={option.value}
              checked={isChecked}
              onChange={handleChange} />
            {!hideCheckmark && <RadioCheckmark />}
            <RadioLabelWrapper>
              <RadioLabel
                css={error ? radioLabelErrorStyle : null}
                htmlFor={ids[index]}
              >
                {option.text}
                {option.note && (
                  <RadioNote>{option.note}</RadioNote>
                )}
              </RadioLabel>
            </RadioLabelWrapper>
          </RadioOption>
        )})}
      <Clear />
    </Wrapper>
  );
}
